const isBrowser = typeof window !== 'undefined';

// --- Helpers ---------------------------------------------------------------
function safeB64UrlToB64(seg: unknown): string {
  // JWT uses base64url without padding; convert → base64 with correct padding.
  if (typeof seg !== 'string') return '';
  let s = seg.replace(/-/g, '+').replace(/_/g, '/');
  const pad = s.length % 4;
  if (pad) s += '='.repeat(4 - pad);
  return s;
}

function b64Decode(s: string): string {
  if (isBrowser && typeof atob === 'function') {
    return atob(s);
  }
  // SSR / Node fallback
  // eslint-disable-next-line no-undef
  return Buffer.from(s, 'base64').toString('binary');
}

function parseJwtPayload(token: string): any | null {
  try {
    const parts = String(token || '').split('.');
    if (parts.length !== 3) return null;
    const payloadB64 = safeB64UrlToB64(parts[1]);
    if (!payloadB64) return null;
    const json = b64Decode(payloadB64);
    if (!json) return null;
    return JSON.parse(json);
  } catch {
    return null;
  }
}

function getCookie(name: string): string | null {
  if (!isBrowser || !document?.cookie) return null;
  const m = document.cookie.match(new RegExp(`(?:^|; )${name.replace(/([$()*+./?[\\\]^{|}-])/g, '\\$1')}=([^;]*)`));
  return m ? decodeURIComponent(m[1]) : null;
}

function safeStorageGet(key: string): string | null {
  if (!isBrowser) return null;
  try { return window.localStorage.getItem(key); } catch { return null; }
}

function safeSessionGet(key: string): string | null {
  if (!isBrowser) return null;
  try { return window.sessionStorage.getItem(key); } catch { return null; }
}

function firstNonEmpty(...vals: Array<string | null | undefined>): string | null {
  for (const v of vals) if (v && String(v).length > 0) return String(v);
  return null;
}

function normalizeToken(t: string | null): string | null {
  if (!t) return null;
  let v = t.trim();
  if (v.toLowerCase().startsWith('bearer ')) v = v.slice(7).trim();
  // strip accidental surrounding quotes
  v = v.replace(/^"+|"+$/g, '');
  return v || null;
}

function getAuthTokenFromAnywhere(): string | null {
  // honor explicit app cookie name first
  const fromCookie =
    getCookie('faang_token') ||
    getCookie('token') ||
    getCookie('authToken') ||
    getCookie('accessToken') ||
    getCookie('jwt');
  const fromStorage =
    safeStorageGet('faang_token') ||
    safeStorageGet('token') ||
    safeStorageGet('authToken') ||
    safeStorageGet('accessToken') ||
    safeStorageGet('jwt');
  return normalizeToken(firstNonEmpty(fromStorage, fromCookie));
}

export function isPublicRoute(pathname?: string): boolean {
  const path = (pathname || (isBrowser ? window.location.pathname : '')).toLowerCase();
  if (!path) return false;
  // Handle both top-level and /auth/* versions
  const PUBLIC = new Set([
    '/', // root should never force tenant headers
    '/login', '/register', '/forgot-password', '/reset-password',
    '/auth/login', '/auth/register', '/auth/forgot-password', '/auth/reset-password',
    // e-mail verification flows (align with API), keep public
    '/verify-email', '/auth/verify-email', '/request-verify-email', '/auth/request-verify-email',

  ]);
  return PUBLIC.has(path);
}

export function decodeTenantId(token: string | null): string | null {
  if (!token) return null;
  const payload = parseJwtPayload(token);
  if (!payload) return null;
  return payload.tenantId || payload.tenant_id || null;
}

export function getTenantId(state?: {
  tenant?: { id?: string | null };
  auth?: { token?: string | null; user?: { tenantId?: string | null; tenant_id?: string | null } | null };
}): string | null {
  if (isPublicRoute()) return null;
  const fromTenantSlice = state?.tenant?.id ?? null;
  if (fromTenantSlice) return fromTenantSlice;
  const fromAuth =
    state?.auth?.user?.tenantId ??
    state?.auth?.user?.tenant_id ??
    null;
  if (fromAuth) return fromAuth;

  // Try state token → storage/cookie token
  const token =
    state?.auth?.token ||
    getAuthTokenFromAnywhere();
  const decoded = decodeTenantId(token);
  // If decode fails, fall back to stored tenantId (legacy)
  const stored = isBrowser ? safeStorageGet('tenantId') : null;
  const result = decoded || stored || null;
  // Warn only in the browser (avoid SSG/SSR build log spam) if still missing
  if (!result && isBrowser) {
    console.warn('[tenantContext] tenantId missing', { state });
  }
  return result;
}

function inferSubdomainFromHost(hostname: string): string | null {
  if (!hostname) return null;
  const HARMLESS = new Set(['localhost', '127.0.0.1', '::1', 'www', '']);
  const BAD_ROOTS = new Set(['project2']); // align with API fallback list
  try {
    const parts = hostname.split('.');
    // e.g. foo.example.com → ['foo','example','com']
    if (parts.length >= 3) {
      const first = parts[0]?.toLowerCase();
      if (first && !HARMLESS.has(first) && !BAD_ROOTS.has(first)) return first;
      return null;
    }
    // For localhost or bare domains: treat as no subdomain
    return null;
  } catch {
    return null;
  }
}

export function getSubdomain(state?: { tenant?: { subdomain?: string | null } }): string | null {
  if (isPublicRoute()) return null;
  // 1) Redux slice
  const fromState = state?.tenant?.subdomain ?? null;
  if (fromState) return fromState;
  // 2) session/local storage
  const fromStorage =
    isBrowser
      ? (safeSessionGet('subdomain') || safeStorageGet('subdomain'))
      : null;
  if (fromStorage) return fromStorage;
  // 3) JWT fallback
  if (isBrowser) {
    try {
      const tk = getAuthTokenFromAnywhere();
      if (tk) {
        const payload = parseJwtPayload(tk);
        if (payload?.subdomain) return String(payload.subdomain);
      }
    } catch { /* ignore */ }
    // 4) URL / hostname inference
    try {
      const params = new URLSearchParams(window.location.search);
      const q =
        params.get('subdomain') ||
        params.get('tenant') ||
        params.get('t');
      if (q) return q;
      const inferred = inferSubdomainFromHost(window.location.hostname || '');
      if (inferred) return inferred;  
    } catch { /* ignore */ }
    console.warn('[tenantContext] subdomain missing', { state });
  }
  return null;
}

/**
 * Build consistent auth/tenant headers for API requests from the web app.
 * - Authorization: Bearer <token> (if token present)
 * - X-Subdomain / X-Tenant-Id populated when available
 */
export function buildTenantHeaders(
  state?: {
    tenant?: { id?: string | null; subdomain?: string | null };
    auth?: { token?: string | null; user?: { tenantId?: string | null; tenant_id?: string | null } | null };
  },
  tokenOverride?: string | null
): Record<string, string> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  const token = tokenOverride ?? (state?.auth?.token ?? getAuthTokenFromAnywhere());
  const tenantId = getTenantId(state);
  const subdomain = getSubdomain(state);
  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (tenantId) headers['X-Tenant-Id'] = tenantId;
  if (subdomain) headers['X-Subdomain'] = subdomain;
  return headers;
}
